import {
  MUSIC_VIDEO_MAX_CUSTOM_TEXTS,
  MUSIC_VIDEO_MIN_CLIP_SEC,
  MUSIC_VIDEO_TEXT_MAX_LENGTH,
  type MusicVideoCustomText,
  type MusicVideoDoc,
} from 'fym-shared/src/music24/musicVideoDoc';

/** A new text holds this long (design d2: "+ Add text at 0:51"). */
export const CUSTOM_TEXT_SEC = 4;

/** The fixed texts with a switch (design d2). */
export type FixedTextKey = 'title' | 'name' | 'endCard';

/** Turns a fixed text on or off. Pure. */
export function toggleFixedText(doc: MusicVideoDoc, key: FixedTextKey): MusicVideoDoc {
  const texts = { ...doc.texts };
  if (key === 'endCard') {
    texts.endCard = { ...texts.endCard, on: !texts.endCard.on };
  } else {
    texts[key] = { ...texts[key], on: !texts[key].on };
  }
  return { ...doc, texts };
}

/** Turns the song info corner on or off. Pure. */
export function toggleSongInfoCorner(doc: MusicVideoDoc): MusicVideoDoc {
  return { ...doc, texts: { ...doc.texts, songInfoCorner: !doc.texts.songInfoCorner } };
}

/** The own text of the title or the name; empty shows the song value. Pure. */
export function setFixedTextOverride(doc: MusicVideoDoc, key: 'title' | 'name', text: string): MusicVideoDoc {
  return {
    ...doc,
    texts: { ...doc.texts, [key]: { ...doc.texts[key], text: text.slice(0, MUSIC_VIDEO_TEXT_MAX_LENGTH) } },
  };
}

/**
 * Adds a text at `atSec` for `CUSTOM_TEXT_SEC` (less at the end of the video). Nothing changes at
 * the limit of `MUSIC_VIDEO_MAX_CUSTOM_TEXTS` or with no room. Pure.
 */
export function addCustomText(doc: MusicVideoDoc, input: { id: string; text: string; atSec: number }): MusicVideoDoc {
  const { startSec: rangeStart, endSec: rangeEnd } = doc.settings;
  if (doc.texts.custom.length >= MUSIC_VIDEO_MAX_CUSTOM_TEXTS) {
    return doc;
  }
  const startSec = Math.min(Math.max(input.atSec, rangeStart), rangeEnd - MUSIC_VIDEO_MIN_CLIP_SEC);
  const endSec = Math.min(rangeEnd, startSec + CUSTOM_TEXT_SEC);
  if (endSec - startSec < MUSIC_VIDEO_MIN_CLIP_SEC) {
    return doc;
  }
  const text: MusicVideoCustomText = {
    id: input.id,
    text: input.text.slice(0, MUSIC_VIDEO_TEXT_MAX_LENGTH),
    startSec,
    endSec,
  };
  const custom = [...doc.texts.custom, text].sort((a, b) => a.startSec - b.startSec);
  return { ...doc, texts: { ...doc.texts, custom } };
}

/** Changes a text the user added. The times stay inside the video and at least 0.25 s long. Pure. */
export function updateCustomText(
  doc: MusicVideoDoc,
  id: string,
  patch: Partial<Pick<MusicVideoCustomText, 'text' | 'startSec' | 'endSec'>>
): MusicVideoDoc {
  const { startSec: rangeStart, endSec: rangeEnd } = doc.settings;
  const custom = doc.texts.custom
    .map((item) => {
      if (item.id !== id) {
        return item;
      }
      const startSec = Math.min(
        Math.max(patch.startSec ?? item.startSec, rangeStart),
        rangeEnd - MUSIC_VIDEO_MIN_CLIP_SEC
      );
      const endSec = Math.min(Math.max(patch.endSec ?? item.endSec, startSec + MUSIC_VIDEO_MIN_CLIP_SEC), rangeEnd);
      return { ...item, text: (patch.text ?? item.text).slice(0, MUSIC_VIDEO_TEXT_MAX_LENGTH), startSec, endSec };
    })
    .sort((a, b) => a.startSec - b.startSec);
  return { ...doc, texts: { ...doc.texts, custom } };
}

export function removeCustomText(doc: MusicVideoDoc, id: string): MusicVideoDoc {
  return { ...doc, texts: { ...doc.texts, custom: doc.texts.custom.filter((item) => item.id !== id) } };
}
